import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { FormField } from '@/shared/components/crud/form-field'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/shared/components/ui/select'

export interface CompanyOption {
  Id: number
  Name: string
}

export interface CurrencyOption {
  id: number
  isoCode: string
}

interface WizardCompanyFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  companies: CompanyOption[]
}

/** Shared Company picker used by both the create and edit account field forms. */
export function WizardCompanyField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ control, name, companies }: WizardCompanyFieldProps<TFieldValues, TName>) {
  return (
    <FormField control={control} name={name} label="Company" required>
      {() => (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Select
              value={field.value ? String(field.value) : ''}
              onValueChange={(value) => field.onChange(Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.Id} value={String(company.Id)}>{company.Name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      )}
    </FormField>
  )
}

interface WizardCurrencyFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  currencies: CurrencyOption[]
  disabled?: boolean
}

/** Shared Currency picker used by both the create and edit account field forms.
 * `disabled` supports the edit form's not-always-editable currency rule. */
export function WizardCurrencyField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ control, name, currencies, disabled = false }: WizardCurrencyFieldProps<TFieldValues, TName>) {
  return (
    <FormField control={control} name={name} label="Currency" required>
      {() => (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Select
              value={field.value ? String(field.value) : ''}
              onValueChange={(value) => field.onChange(Number(value))}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select currency…" />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency.id} value={String(currency.id)}>{currency.isoCode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      )}
    </FormField>
  )
}
